import {
  findSchedulesByPartnerId,
  findSchedulesByProfessionalId,
} from '@/app/(main)/(pages)/_services/search-api/search-api.service';
import {
  SearchApiSchedule,
  SearchApiScheduleTypeKey,
  SearchApiServiceChannel,
} from '@/app/(main)/(pages)/_types/search-api-response.type';
import { ScheduleType } from '@/app/(main)/(pages)/_components/schedules/_types/schedule-type.enum';
import { CartItem, CartScheduleSlot } from '@/types/cart.types';

const SCHEDULES_PAGE_SIZE = 50;

const SEARCH_API_SCHEDULE_TYPE_KEYS: Record<ScheduleType, SearchApiScheduleTypeKey> = {
  [ScheduleType.SCHEDULED]: 'scheduled',
  [ScheduleType.ARRIVAL_ORDER]: 'arrivalOrder',
  [ScheduleType.FLEX]: 'flex',
  [ScheduleType.LABORATORIAL]: 'laboratorial',
};

function toScheduleMap(schedules: SearchApiSchedule[] | undefined): Map<string, SearchApiSchedule> {
  return new Map((schedules ?? []).filter((schedule) => schedule.id).map((schedule) => [schedule.id, schedule]));
}

function hasSameAgeRange(schedule: SearchApiSchedule, slot: CartScheduleSlot): boolean {
  return (schedule.minAge ?? null) === (slot.minAge ?? null)
    && (schedule.maxAge ?? null) === (slot.maxAge ?? null);
}

function pickSharedSchedule(
  candidates: Map<string, SearchApiSchedule>[],
  slot: CartScheduleSlot,
): string | undefined {
  const [first, ...rest] = candidates;

  if (!first) {
    return undefined;
  }

  const shared = [...first.values()].filter((schedule) => rest.every((ids) => ids.has(schedule.id)));

  return (shared.find((schedule) => hasSameAgeRange(schedule, slot)) ?? shared[0])?.id;
}

async function resolveByProfessional(
  professionalId: string,
  items: CartItem[],
  scheduleTypeKey: SearchApiScheduleTypeKey,
  serviceChannel: SearchApiServiceChannel,
): Promise<string | undefined> {
  const partnerFacilityId = items[0].partner.facility.id;

  const { groups } = await findSchedulesByProfessionalId(professionalId, {
    partnerFacilityId,
    procedureIds: [...new Set(items.map((item) => item.procedure.id))],
    serviceChannel,
    limit: SCHEDULES_PAGE_SIZE,
  });

  return pickSharedSchedule(
    items.map((item) => toScheduleMap(
      groups.find((group) => group.procedure.id === item.procedure.id)
        ?.schedulesByScheduleType?.[scheduleTypeKey],
    )),
    items[0].slot,
  );
}

async function resolveByPartner(
  items: CartItem[],
  scheduleTypeKey: SearchApiScheduleTypeKey,
  serviceChannel: SearchApiServiceChannel,
): Promise<string | undefined> {
  const [firstItem] = items;
  const partnerFacilityId = firstItem.partner.facility.id;
  const procedureIds = [...new Set(items.map((item) => item.procedure.id))];

  const { groups } = await findSchedulesByPartnerId(firstItem.partner.id, {
    partnerFacilityId,
    procedureIds,
    serviceChannel,
    limit: SCHEDULES_PAGE_SIZE,
  });

  return pickSharedSchedule(
    procedureIds.map((procedureId) => {
      const options = groups.find((group) => group.procedure.id === procedureId)?.options ?? [];

      return new Map(
        options
          .filter((option) => option.partnerFacilityId === partnerFacilityId && !option.professionalId)
          .flatMap((option) => [...toScheduleMap(option.schedulesByScheduleType?.[scheduleTypeKey])]),
      );
    }),
    firstItem.slot,
  );
}

export async function resolveCartItemsScheduleId(
  items: CartItem[],
  scheduleType: ScheduleType,
): Promise<string | undefined> {
  const [firstItem] = items;

  if (!firstItem) {
    return undefined;
  }

  const scheduleTypeKey = SEARCH_API_SCHEDULE_TYPE_KEYS[scheduleType];

  const serviceChannel = firstItem.procedure.isTelehealth
    ? SearchApiServiceChannel.TELEHEALTH
    : SearchApiServiceChannel.IN_PERSON;

  const professionalId = firstItem.professional?.id;

  return professionalId
    ? resolveByProfessional(professionalId, items, scheduleTypeKey, serviceChannel)
    : resolveByPartner(items, scheduleTypeKey, serviceChannel);
}
